import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import './layout.css';

export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setScrolled(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 641px)');
    const closeOnDesktop = (event) => {
      if (event.matches) setMenuOpen(false);
    };
    media.addEventListener('change', closeOnDesktop);
    return () => media.removeEventListener('change', closeOnDesktop);
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen]);

  const isResume = location.pathname === '/resume';

  return (
    <div data-theme="dark" className={isResume ? 'is-resume' : undefined}>
      <div className={`site-chrome${scrolled ? ' is-scrolled' : ''}`}>
        <header ref={headerRef}>
          <h1>Mike Moe</h1>
          <p>Software Engineering · Game Dev · Fullstack</p>
          <img
            src="/favicon.png"
            alt="Pixel art Konlet portrait of Mike Moe"
            className="header-portrait"
            width={96}
            height={96}
          />
        </header>
        <nav className={menuOpen ? 'menu-open' : undefined}>
          <div className="nav-container">
            <button
              type="button"
              className={`hamburger-menu${menuOpen ? ' open' : ''}`}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls="site-nav-links"
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span />
              <span />
              <span />
            </button>
            <Link to="/" className="nav-brand">Mike Moe</Link>
            <ul id="site-nav-links">
              <li><NavLink to="/" end>Overview</NavLink></li>
              <li><NavLink to="/game-dev">Game Dev</NavLink></li>
              <li><NavLink to="/fullstack">Fullstack</NavLink></li>
              <li><NavLink to="/other-dev">Other Dev</NavLink></li>
              <li><NavLink to="/blog">Blog</NavLink></li>
              <li><NavLink to="/contact">Contact</NavLink></li>
            </ul>
            <div className="nav-right">
              <a
                href="https://twitter.com/MikeMoeDev"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                aria-label="Twitter"
                title="Twitter"
              >
                {/* Twitter bird icon */}
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="currentColor" d="M22.46 6c-.77.35-1.6.58-2.46.69.89-.53 1.57-1.38 1.89-2.39-.83.5-1.76.86-2.74 1.06A4.13 4.13 0 0 0 16.1 4c-2.28 0-4.13 1.91-4.13 4.26 0 .33.03.64.1.94-3.43-.18-6.47-1.89-8.5-4.5-.36.64-.57 1.38-.57 2.17 0 1.5.73 2.82 1.85 3.6-.67-.02-1.3-.21-1.85-.52v.05c0 2.1 1.44 3.85 3.35 4.25-.35.1-.72.15-1.1.15-.27 0-.53-.03-.78-.08.53 1.73 2.06 2.99 3.89 3.02A8.2 8.2 0 0 1 2 19.55a11.55 11.55 0 0 0 6.38 1.9c7.65 0 11.83-6.57 11.83-12.27 0-.19 0-.38-.01-.56.81-.6 1.52-1.36 2.06-2.22z" />
                </svg>
              </a>
              <a
                href="https://github.com/konlet"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                aria-label="GitHub"
                title="GitHub"
              >
                {/* GitHub octocat silhouette */}
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="currentColor" d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.387.6.11.82-.26.82-.577 0-.285-.01-1.04-.016-2.04-3.338.726-4.042-1.61-4.042-1.61-.546-1.387-1.333-1.756-1.333-1.756-1.09-.745.083-.73.083-.73 1.205.085 1.84 1.237 1.84 1.237 1.07 1.834 2.807 1.304 3.492.997.108-.775.418-1.305.76-1.605-2.665-.304-5.467-1.332-5.467-5.93 0-1.31.468-2.382 1.236-3.22-.124-.304-.536-1.524.117-3.176 0 0 1.008-.322 3.3 1.23a11.5 11.5 0 0 1 3.003-.404c1.02.005 2.047.138 3.006.404 2.29-1.552 3.296-1.23 3.296-1.23.655 1.652.243 2.872.12 3.176.77.838 1.235 1.91 1.235 3.22 0 4.61-2.807 5.624-5.48 5.921.43.372.823 1.102.823 2.222 0 1.606-.015 2.898-.015 3.293 0 .32.218.694.825.576C20.565 21.796 24 17.297 24 12 24 5.37 18.627 0 12 0z" />
                </svg>
              </a>
            </div>
          </div>
        </nav>
      </div>
      {menuOpen ? (
        <button
          type="button"
          className="nav-overlay"
          aria-label="Close menu"
          onClick={() => setMenuOpen(false)}
        />
      ) : null}
      <main>
        <div className="content-wrapper">
          <div className="main-content">
            <Outlet />
          </div>
        </div>
      </main>
      <footer>
        <p>© {new Date().getFullYear()} Mike Moe. All rights reserved.</p>
      </footer>
    </div>
  );
}


